import { apiService } from './apiService';
import { User } from '../types/auth';
import {
  TrainingModule,
  Exam,
  Question,
  TrainingAssignment,
  PractitionerListItem,
  DashboardSummary,
  SMTPConfig,
} from '../types/training';

class AdminService {
  // Practitioner Management
  async registerPractitioner(data: {
    email: string;
    name: string;
    phoneNumber: string;
  }): Promise<User> {
    return apiService.post<User>('/auth/register', data);
  }

  async resetPractitionerPassword(id: string): Promise<{ temporaryPassword: string }> {
    return apiService.post<{ temporaryPassword: string }>(`/admin/practitioners/${id}/password/reset`, {});
  }

  async getPractitioners(): Promise<PractitionerListItem[]> {
    // Fetch practitioners and assignments in parallel
    const [users, assignments] = await Promise.all([
      apiService.get<User[]>('/admin/practitioners'),
      apiService.get<TrainingAssignment[]>('/assignments')
    ]);
    
    // Combine user data with assignment statistics
    return users.map(user => {
      const userAssignments = assignments.filter(a => a.practitionerId === user.id);
      const completed = userAssignments.filter(a => a.status === 'completed').length;
      const pending = userAssignments.filter(a => a.status === 'pending' || a.status === 'in_progress').length;
      const flagged = userAssignments.filter(a => a.flagged).length;
      
      // Find most recent activity (use completedAt if available, otherwise assignedAt)
      const lastActivity = userAssignments.length > 0
        ? userAssignments.reduce((latest, a) => {
            const aDate = new Date(a.completedAt || a.assignedAt);
            return aDate > new Date(latest) ? (a.completedAt || a.assignedAt) : latest;
          }, userAssignments[0].completedAt || userAssignments[0].assignedAt)
        : new Date(user.createdAt).toISOString();
      
      return {
        id: user.id,
        name: user.name,
        email: user.email,
        phoneNumber: user.phoneNumber || '',
        assignedTrainings: userAssignments.length,
        completedTrainings: completed,
        pendingTrainings: pending,
        flaggedTrainings: flagged,
        lastActivity: lastActivity,
      };
    });
  }

  async getPractitioner(id: string): Promise<User> {
    return apiService.get<User>(`/dashboard/${id}`);
  }

  async updatePractitioner(id: string, data: Partial<User>): Promise<User> {
    return apiService.put<User>(`/auth/users/${id}`, data);
  }

  async deletePractitioner(id: string): Promise<void> {
    return apiService.delete<void>(`/auth/users/${id}`);
  }

  // Training Module Management
  async createModule(data: {
    title: string;
    description: string;
  }): Promise<TrainingModule> {
    return apiService.post<TrainingModule>('/content/modules', data);
  }

  async getModules(includeArchived: boolean = false): Promise<TrainingModule[]> {
    const params = includeArchived ? { includeArchived: 'true' } : {};
    return apiService.get<TrainingModule[]>('/content/modules', params);
  }

  async getModule(id: string): Promise<TrainingModule> {
    return apiService.get<TrainingModule>(`/admin/modules/${id}`);
  }

  async updateModule(id: string, data: Partial<TrainingModule>): Promise<TrainingModule> {
    return apiService.put<TrainingModule>(`/admin/modules/${id}`, data);
  }

  async archiveModule(id: string): Promise<{ message: string; archivedAt: string }> {
    return apiService.delete<{ message: string; archivedAt: string }>(`/admin/modules/${id}`);
  }

  async deleteModule(id: string): Promise<void> {
    // Call archiveModule internally for backward compatibility
    await this.archiveModule(id);
  }

  async uploadContent(moduleId: string, file: File, onProgress?: (progress: number) => void): Promise<any> {
    // For large files, use presigned URL upload (direct to S3)
    // For small files (< 5MB), use the old base64 method for backward compatibility
    const LARGE_FILE_THRESHOLD = 5 * 1024 * 1024; // 5MB

    if (file.size >= LARGE_FILE_THRESHOLD) {
      // Step 1: Get presigned upload URL
      const uploadData = await apiService.post<{
        uploadUrl: string;
        fileId: string;
        s3Key: string;
        fileName: string;
        fileType: string;
        contentType: string;
      }>(`/admin/modules/${moduleId}/upload-url`, {
        fileName: file.name,
        contentType: file.type,
        fileSize: file.size,
      });

      // Step 2: Upload directly to S3 using presigned URL
      const xhr = new XMLHttpRequest();
      
      await new Promise<void>((resolve, reject) => {
        xhr.upload.addEventListener('progress', (e) => {
          if (e.lengthComputable && onProgress) {
            const progress = Math.round((e.loaded / e.total) * 100);
            onProgress(progress);
          }
        });

        xhr.addEventListener('load', () => {
          if (xhr.status >= 200 && xhr.status < 300) {
            resolve();
          } else {
            reject(new Error(`Upload failed with status ${xhr.status}`));
          }
        });

        xhr.addEventListener('error', () => {
          reject(new Error('Upload failed'));
        });

        xhr.open('PUT', uploadData.uploadUrl);
        xhr.setRequestHeader('Content-Type', file.type);
        xhr.send(file);
      });

      // Step 3: Confirm upload completion
      return apiService.post(`/admin/modules/${moduleId}/confirm-upload`, {
        fileId: uploadData.fileId,
        fileName: uploadData.fileName,
        s3Key: uploadData.s3Key,
        fileType: uploadData.fileType,
        fileSize: file.size,
      });
    } else {
      // Small file: use base64 upload through API Gateway
      const base64Content = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => {
          const base64 = (reader.result as string).split(',')[1];
          resolve(base64);
        };
        reader.onerror = reject;
        reader.readAsDataURL(file);
      });

      return apiService.post(`/admin/modules/${moduleId}/content`, {
        fileName: file.name,
        fileContent: base64Content,
        contentType: file.type,
      });
    }
  }

  async deleteContent(moduleId: string, fileId: string): Promise<void> {
    return apiService.delete<void>(`/admin/modules/${moduleId}/content/${fileId}`);
  }

  // Exam Management
  async createExam(data: {
    moduleId: string;
    title: string;
    passRate: number;
  }): Promise<Exam> {
    return apiService.post<Exam>('/exams', data);
  }

  async getExam(id: string): Promise<Exam> {
    return apiService.get<Exam>(`/admin/exams/${id}`);
  }

  async updateExam(id: string, data: Partial<Exam>): Promise<Exam> {
    return apiService.put<Exam>(`/admin/exams/${id}`, data);
  }

  async deleteExam(id: string): Promise<void> {
    return apiService.delete<void>(`/admin/exams/${id}`);
  }

  async addQuestion(examId: string, question: Omit<Question, 'id' | 'examId'>): Promise<Question> {
    return apiService.post<Question>(`/admin/exams/${examId}/questions`, question);
  }

  async updateQuestion(examId: string, questionId: string, data: Partial<Question>): Promise<Question> {
    return apiService.put<Question>(`/admin/exams/${examId}/questions/${questionId}`, data);
  }

  async deleteQuestion(examId: string, questionId: string): Promise<void> {
    return apiService.delete<void>(`/admin/exams/${examId}/questions/${questionId}`);
  }

  // Training Assignment Management
  async assignTraining(data: {
    practitionerId: string;
    moduleId: string;
    sendNotification?: boolean;
    practitionerName?: string;
    practitionerEmail?: string;
    moduleName?: string;
  }): Promise<TrainingAssignment> {
    return apiService.post<TrainingAssignment>('/assignments', data);
  }

  async getAssignments(filters?: {
    practitionerId?: string;
    moduleId?: string;
    status?: string;
  }): Promise<TrainingAssignment[]> {
    return apiService.get<TrainingAssignment[]>('/assignments', filters);
  }

  async unassignTraining(assignmentId: string): Promise<void> {
    return apiService.delete<void>(`/admin/assignments/${assignmentId}`);
  }

  async resetAttempts(assignmentId: string, maxAttempts: number): Promise<void> {
    return apiService.post<void>(`/admin/assignments/${assignmentId}/reset`, { newMaxAttempts: maxAttempts });
  }

  // Dashboard
  async getDashboardSummary(): Promise<DashboardSummary> {
    // Use the admin dashboard summary endpoint
    return apiService.get<DashboardSummary>('/admin/dashboard/summary');
  }

  // SMTP Configuration
  async getSMTPConfig(): Promise<SMTPConfig> {
    // Use notification config endpoint
    return apiService.get<SMTPConfig>('/notification/config');
  }

  async updateSMTPConfig(config: SMTPConfig): Promise<void> {
    // Use notification config endpoint
    return apiService.post<void>('/notification/config', config);
  }

  async testSMTPConnection(config?: SMTPConfig): Promise<{ success: boolean; message: string }> {
    // Use notification config test endpoint
    return apiService.post<{ success: boolean; message: string }>('/notification/config/test', config);
  }
}

export const adminService = new AdminService();


// Audit Log Types
export interface AuditLog {
  id: string;
  userId: string;
  userRole: string;
  action: string;
  resourceType: string;
  resourceId: string;
  ipAddress: string;
  timestamp: string;
  details?: Record<string, unknown>;
  // Enriched fields for human-readable display
  userName?: string;
  userEmail?: string;
  resourceName?: string;
}

export interface AuditLogFilter {
  userId?: string;
  action?: string;
  resourceType?: string;
  startDate?: string;
  endDate?: string;
  limit?: number;
}

export interface AuditLogResponse {
  logs: AuditLog[];
  count: number;
}

// Add audit methods to AdminService class - these need to be added inside the class
// For now, export standalone functions
export async function getAuditLogs(filters?: AuditLogFilter): Promise<AuditLogResponse> {
  const params: Record<string, string> = {};
  if (filters?.userId) params.userId = filters.userId;
  if (filters?.action) params.action = filters.action;
  if (filters?.resourceType) params.resourceType = filters.resourceType;
  if (filters?.startDate) params.startDate = filters.startDate;
  if (filters?.endDate) params.endDate = filters.endDate;
  if (filters?.limit) params.limit = String(filters.limit);
  
  return apiService.get<AuditLogResponse>('/audit/logs', params);
}
